import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { isChunkLoadError, recoverFromChunkLoadError } from 'utils/monitoring/chunkLoadError';

interface FallbackProps {
	error: Error;
	resetError: () => void;
}

/**
 * Rendered in place of the app once the boundary has caught an error.
 *
 * This MUST be mounted as a real component (i.e. through JSX), never handed to
 * Sentry.ErrorBoundary as its `fallback` prop directly: the SDK calls a function
 * fallback inline from its own class `render()`, so the hooks below would run
 * outside a function component and throw "Invalid hook call" — taking the
 * boundary down with them and leaving the user a blank page. See AUDIT-ADMIN-1TV.
 */
export const ErrorBoundaryFallback = ({ error, resetError }: FallbackProps): React.ReactElement => {
	const isChunkError = isChunkLoadError(error?.message);

	useEffect(() => {
		if (isChunkError) {
			// Sentry.ErrorBoundary already captures this exception itself; skip the manual report.
			recoverFromChunkLoadError(error, true);
		}
	}, [isChunkError, error]);

	if (isChunkError) {
		return <div style={{ padding: 40, textAlign: 'center' }}>Reloading…</div>;
	}
	return (
		<div style={{ padding: 40, textAlign: 'center' }}>
			<h2>Something went wrong.</h2>
			<button onClick={resetError}>Try again</button>
		</div>
	);
};

const renderFallback: Sentry.FallbackRender = ({ error, resetError }) => (
	<ErrorBoundaryFallback error={error} resetError={resetError} />
);

/**
 * Last-resort boundary around the whole app. Keeps the boundary and its fallback
 * together so the fallback is always mounted as a component rather than invoked.
 */
const AppErrorBoundary = ({ children }: { children: React.ReactNode }): React.ReactElement => (
	<Sentry.ErrorBoundary
		fallback={renderFallback}
		beforeCapture={(scope, error) => {
			if (isChunkLoadError(error?.message)) {
				scope.setTag('chunkLoadRecovery', true);
			}
		}}
	>
		{children}
	</Sentry.ErrorBoundary>
);

export default AppErrorBoundary;
